/*
 * Copyright (c) 2020. American Express Company
 */

import { LocalDate } from '@js-joda/core';
import _ from 'lodash';
import { Board, SortOrder } from 'src/bw/board/Board';
import { CategoryModel } from 'src/types/category.model';
import { RecurringExpenseModel } from 'src/types/recurring-expense.model';
import { TransactionModel } from 'src/types/transaction.model';
import { Summary } from 'src/types/user.model';

import CashFlowService from '../../services/cashflow.service';
import CategoryService from '../../services/category.service';
import FormatService from '../../services/format.service';
import MixpanelService from '../../services/mixpanel.service';
import NavigationService from '../../services/navigation.service';
import RecurringExpenseService from '../../services/recurring-expense.service';
import TransactionService from '../../services/transaction.service';

export default class ExpensesFactory {
    private readonly component: Board;

    constructor(component: any) {
        this.component = component;
    }

    private static addUrlParameter(url: string, extra: string): string {
        return url + (!extra ? '' : !url.includes('?') ? '?' + extra : '&' + extra);
    }

    public async handleOpenTransactions(
        id: string,
        startDate: LocalDate,
        endDate: LocalDate,
        params: string = ''
    ): Promise<any> {
        void NavigationService.goTo(
            this.component.props.history,
            ExpensesFactory.addUrlParameter(
                ExpensesFactory.addUrlParameter(
                    `/drawer/transactions?start=${FormatService.formatSqlDate(
                        startDate
                    )}&end=${FormatService.formatSqlDate(endDate)}`,
                    id ? `group=${id}` : ''
                ),
                params
            ),
            'Transactions drawer'
        );
    }

    public async openTransactionsRoute(id: string, startDate: LocalDate, endDate: LocalDate): Promise<any> {
        if (!id) {
            return;
        }

        let transactions: TransactionModel[] = await TransactionService.getTransactionsForCategory(
            startDate,
            endDate,
            id
        );

        transactions = _.filter(transactions, (transaction: TransactionModel) => {
            return transaction.type === 'TRANSACTION_EXPENSE';
        });

        const categories: CategoryModel[] = await CategoryService.getCategories();
        const category = _.find(categories, { id });
        const accounts = this.component.props.user.accounts;
        const sort: SortOrder = { sort: 'date', order: 1 };

        transactions = await this.component.transactionFactory.sortTransactions(transactions, sort.sort, sort.order);

        this.component.setState({
            drawerState: 'transactions',
            drawerProps: {
                accounts,
                transactionsCategoryId: id,
                transactionsCategory: category.name,
                transactions,
                transactionSort: sort,
                transactionsChanged: false,
                category: this.component.state.transactionsCategory,
                selectedTransaction: null,
                selectedTransactionGroup: this.component.state.selectedTransactionGroup,
                startDate,
                endDate,
                zoom: this.component.state.zoom,
                type: 'expenses',
                categories
            }
        });
    }

    public filterSummaries(startDate: LocalDate, endDate: LocalDate): Summary[] {
        if (!startDate || !endDate) return;

        const startMonths: number = startDate.year() * 12 + startDate.monthValue();
        const endMonths: number = endDate.year() * 12 + endDate.monthValue();

        let summaries: any = _.filter(this.component.props.user.rawSummaries, function (summary) {
            const months: number = (summary.year as number) * 12 + (summary.month as number);
            return months >= startMonths && months < endMonths;
        });

        const summariesByName: any = _.groupBy(summaries, 'name');
        summaries = _.map(summariesByName, (v, k) => ({ name: k, sum: _.sumBy(v, 'sum') }));

        return summaries;
    }

    public async handleOpenExpenses(startDate: LocalDate, endDate: LocalDate): Promise<void> {
        if (!startDate || !endDate) {
            const offset: number = CashFlowService.getCashFlowIndexWithDate(
                this.component.props.user.rawCashFlow,
                LocalDate.now()
            );
            startDate = this.component.props.user.rawCashFlow[offset - 12].startDate;
            endDate = this.component.props.user.rawCashFlow[offset - 1].endDate;
        }
        void NavigationService.goTo(
            this.component.props.history,
            `/drawer/expenses/${FormatService.formatSqlDate(startDate)}/${FormatService.formatSqlDate(endDate)}`,
            'Transactions drawer'
        );
    }

    public async openExpensesSummaryRoute(startDate: LocalDate, endDate: LocalDate): Promise<void> {
        const recurringExpenses: RecurringExpenseModel[] = await RecurringExpenseService.getRecurringExpenses();

        this.component.setState({
            drawerState: 'expenses',
            drawerProps: {
                startDate,
                endDate,
                summaries: await TransactionService.getTransactionSummaries(startDate, endDate),
                categories: await CategoryService.getCategories(),
                zoom: this.component.state.zoom,
                recurringExpenses
            }
        });
        window.dispatchEvent(new Event('resize'));
    }

    public async handleAddExpense(): Promise<void> {
        this.component.drawerFactory.setDrawerRoute('expense', {});
    }

    public async handleOpenRecurringExpense(recurringExpense: RecurringExpenseModel): Promise<void> {
        void NavigationService.goTo(
            this.component.props.history,
            `/drawer/expense/${recurringExpense.id}`,
            'Expense' + ' drawer'
        );
    }

    public async handleOpenReviewExpenseTransactions(mentorInitiated: boolean): Promise<void> {
        void NavigationService.goTo(
            this.component.props.history,
            `/review/expenses/${mentorInitiated}`,
            'Review Expenses drawer'
        );
    }

    public async openReviewExpenseTransactionsRoute(mentorInitiated: boolean): Promise<any> {
        const transactions: TransactionModel[] = _.filter(
            await TransactionService.getTransactionsForReview(),
            (transaction: TransactionModel) => {
                return transaction.type === 'TRANSACTION_EXPENSE';
            }
        );

        const categories: CategoryModel[] = await CategoryService.getCategories();
        const accounts = this.component.props.user.accounts;
        const sort: SortOrder = { sort: 'date', order: 1 };

        let back: () => any;
        if (mentorInitiated) {
            back = async () => {
                await this.component.handleUpdateUserAttribute('reviewedExpenses', true);
                await this.component.handleReloadUser();
                this.component.drawerFactory.handleTopDrawer();
                void this.component.mentorFactory.processAction('done-review-expenses');
            };
        }

        this.component.setState({
            drawerState: 'review-transactions',
            drawerProps: {
                type: 'expenses',
                transactions,
                transactionSort: sort,
                transactionsChanged: false,
                selectedTransaction: null,
                selectedTransactionGroup: null,
                accounts,
                handleBack: back,
                categories
            }
        });

        MixpanelService.track('Reviewed Expenses', {
            'Transactions To Review': this.component.state.user.expenseTransactionsToReviewCount,
            'Mentor Initiated': mentorInitiated
        });
    }
}
